import { useTranslation } from 'react-i18next'
import { FormikProvider } from 'formik'
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Box,
  IconButton,
  MenuItem,
} from '@mui/material'

import { Close } from '@mui/icons-material'
import { useWalletsDialog, useWalletsForm } from '@/views/wallets/hooks'
import { BonusTriggerType } from '@/enums/be-bonus-trigger-type.enum'

import InputFormikField from '@/views/components/input-formik-field/index.component'
import {
  DepositTriggerFormComponent,
  ManualTriggerFormComponent,
  ByCodeTriggerFormComponent,
} from './'
import { DialogsEnum } from '@/enums'
import { useEffect } from 'react'

export const WalletsFormDialog = () => {
  const { t } = useTranslation()

  const { dialog, toggleDialog } = useWalletsDialog()

  const {
    formikManualWallet,
    bonusTriggerTypes,
    bonusType,
    handleOnSubmit,
    handleResetForm,
  } = useWalletsForm()

  const handleCloseDialog = () => {
    toggleDialog(false)
  }

  useEffect(() => {
    if (dialog.open && dialog.id === DialogsEnum.DIALOG_MANUAL_BONUS) {
      handleResetForm()
    }
  }, [dialog.open, dialog.id]) // eslint-disable-line

  const displayBonusTriggerComponent = () => {
    switch (formikManualWallet.values.bonusTrigger) {
      case BonusTriggerType.DEPOSIT_BY_CLICK:
      case BonusTriggerType.DEPOSIT_BY_CODE:
        return <DepositTriggerFormComponent />

      case BonusTriggerType.BY_CODE:
      case BonusTriggerType.GAMIFICATION:
      case BonusTriggerType.BY_REGISTRATION:
        return <ByCodeTriggerFormComponent />

      case BonusTriggerType.MANUAL:
        return (
          <ManualTriggerFormComponent
            bonusType={formikManualWallet.values.bonusType}
          />
        )

      default:
        return <></>
    }
  }

  const isOpen = dialog.open && dialog.id === DialogsEnum.DIALOG_MANUAL_BONUS

  return (
    <Dialog
      open={isOpen}
      aria-labelledby="dialog-title"
      className="form"
      TransitionProps={{
        onExited: () => {
          handleResetForm()
        },
      }}
      fullWidth
    >
      <DialogTitle id="dialog-title">
        {t('wallet.setNewWallet')}
        <IconButton aria-label="close" onClick={handleCloseDialog}>
          <Close />
        </IconButton>
      </DialogTitle>
      <FormikProvider value={formikManualWallet}>
        <DialogContent>
          <Box overflow="hidden">
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <InputFormikField
                  label={t('labels.bonusType')}
                  name="bonusType"
                  select
                  sx={{
                    '& .MuiSelect-select': {
                      display: 'flex',
                      maxHeight: '16px',
                    },
                  }}
                >
                  <MenuItem value="" disabled>
                    <Box fontStyle="italic">{t('selectBonusAmountType')}</Box>
                  </MenuItem>
                  {(bonusType || []).map((item) => (
                    <MenuItem key={item.index} value={item.index}>
                      {t(`bonusTypes.${item.label}`)}
                    </MenuItem>
                  ))}
                </InputFormikField>
              </Grid>
              <Grid item xs={12}>
                <InputFormikField
                  label={t('labels.bonusTrigger')}
                  name="bonusTrigger"
                  select
                  sx={{
                    '& .MuiSelect-select': {
                      display: 'flex',
                      maxHeight: '16px',
                    },
                  }}
                >
                  <MenuItem value="" disabled>
                    <Box fontStyle="italic">{t('selectBonusTriggerType')}</Box>
                  </MenuItem>
                  {(bonusTriggerTypes || []).map((item) => (
                    <MenuItem key={item.index} value={item.index}>
                      {t(`bonusTriggerTypes.${item.label}`)}
                    </MenuItem>
                  ))}
                </InputFormikField>
              </Grid>
            </Grid>
            {displayBonusTriggerComponent()}
          </Box>
        </DialogContent>
      </FormikProvider>
      <DialogActions>
        <Button onClick={handleCloseDialog} className="red" variant="text">
          {t('labels.cancel')}
        </Button>
        <Button
          disabled={!(formikManualWallet.isValid && formikManualWallet.dirty)}
          onClick={handleOnSubmit}
          color="primary"
          variant="contained"
        >
          {t('labels.save')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
